import React from 'react'
import {useTranslation} from 'react-i18next'
import {kebabCase} from 'lodash'

import {ActionRow} from 'src/products/loans/MakePayment/LoanPayConfirm/ActionRow/ActionRow'
import {ActionRowPaymentMethod} from 'src/products/loans/MakePayment/LoanPayConfirm/ActionRow/PaymentMethodActionRow.types'
import {AppEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import {convertToDollarAmt} from 'src/lib/utils/numberUtil'
import {formatDate, friendlyDateWithDOW} from 'src/lib/utils/date'
import {NamedColors} from 'src/designSystem/colors'
import {PaymentMethodActionRow} from 'src/products/loans/MakePayment/LoanPayConfirm/ActionRow/PaymentMethodActionRow'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import Box from 'src/designSystem/components/atoms/Box/Box'
import Page from 'src/designSystem/components/organisms/Page/Page'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {LoanCustomPaymentIntention} from '@possible/cassandra/src/types/types.mobile.generated'
import {PFInfoCapsule} from 'src/designSystem/components/molecules/PFInfoCapsule/PFInfoCapsule'
import {getLoanPaymentOptionLabel} from 'src/products/loans/MakePayment/LoanPay.utils'
import {LoanCustomPaymentOptionWithMembershipOverdueAmount} from 'src/products/loans/MakePayment/LoanPayAmountEntry/LoanPayAmountEntry.types'

/**
 * This template is named MakePaymentConfirmTemplate rather than LoanPayConfirmTemplate because it is agnostic enough to be used for other products in the future.
 *
 * It is co-located with LoanPayConfirm right now because this it the only place it is used.
 *
 * This template displays the payment amount, the payment method, the date the payment will be executed, and a button to submit the payment.
 */

export type MakePaymentConfirmTemplateProps = {
  isLoading: boolean
  isSubmittingPayment: boolean
  onPressSubmitPayment: () => Promise<void>
  onPressViewPaymentMethods: () => void
  selectedPaymentMethod?: ActionRowPaymentMethod
  selectedPaymentOption?: LoanCustomPaymentOptionWithMembershipOverdueAmount
  soonestAvailablePaymentDate?: string
}

const MakePaymentConfirmTemplate: React.FC<MakePaymentConfirmTemplateProps> = (
  props,
): JSX.Element => {
  const {
    isLoading,
    isSubmittingPayment,
    onPressSubmitPayment: handleOnPressSubmitPayment,
    onPressViewPaymentMethods: handleOnPressViewPaymentMethods,
    selectedPaymentMethod,
    selectedPaymentOption,
    soonestAvailablePaymentDate,
  } = props

  const {t} = useTranslation('MakePaymentConfirm')

  const shouldBeDisabled = isLoading || isSubmittingPayment

  const getPaymentDateSubTitle = (): string => {
    if (!selectedPaymentMethod || selectedPaymentMethod.__typename === 'AchPaymentMethod') {
      return t('SoonestDate')
    } else if (selectedPaymentMethod.__typename === 'DebitCardPaymentMethod') {
      return t('WillBeProcessedToday')
    }
    return ''
  }

  const paymentOptionIntentionTestID: string = selectedPaymentOption?.intention
    ? `-${kebabCase(selectedPaymentOption?.intention)}`
    : ''

  const handleOnPressEditPaymentMethod = (): void => {
    TrackAppEvent(
      ManageActiveLoanEvents.make_payment_confirm_edit_payment_method,
      AppEvents.Category.ManageActiveLoan,
    )
    handleOnPressViewPaymentMethods()
  }

  const handleOnPressAddNewPaymentMethod = (): void => {
    TrackAppEvent(
      ManageActiveLoanEvents.make_payment_confirm_add_payment_method,
      AppEvents.Category.ManageActiveLoan,
    )
    handleOnPressViewPaymentMethods()
  }

  const onPressContinue = async (): Promise<void> => {
    if (selectedPaymentMethod) {
      await handleOnPressSubmitPayment()
    } else {
      handleOnPressAddNewPaymentMethod()
    }
  }

  return (
    <Page
      buttonProps={{
        type: 'singleButton',
        primary: {
          text: selectedPaymentMethod ? t('PayNow') : t('RelinkAccount'),
          onPress: onPressContinue,
          disabled: shouldBeDisabled,
          loading: isSubmittingPayment,
          testID: 'make-payment-confirm-submit',
        },
      }}
      banner={(): null => null}
      noHeaderSpacer
      noHorizontalPadding
      smallTopGap
      testID={'make-payment-confirm-template'}
      title={t('PaymentReview')}
      variant={'generic'}
    >
      <ActionRow
        title={convertToDollarAmt(selectedPaymentOption?.amount)}
        subTitle={selectedPaymentOption ? getLoanPaymentOptionLabel(selectedPaymentOption) : ''}
        testID={`payment-option-action-row${paymentOptionIntentionTestID}`}
        isDisabled={shouldBeDisabled}
      />

      {selectedPaymentMethod ? (
        <PaymentMethodActionRow
          testID={'make-payment-view-payment-methods'}
          paymentMethod={selectedPaymentMethod}
          onPressViewPaymentMethods={handleOnPressEditPaymentMethod}
          isDisabled={shouldBeDisabled}
        />
      ) : (
        <ActionRow
          testID={'unlinked-bank-action-row'}
          isDisabled={shouldBeDisabled}
          title={t('AddAPaymentMethod')}
          titleColor={'error'}
          subTitle={t('LinkABankAccount')}
          onPress={handleOnPressAddNewPaymentMethod}
        />
      )}

      <ActionRow
        testID={'payment-date-action-row'}
        isDisabled={shouldBeDisabled}
        title={formatDate(soonestAvailablePaymentDate, friendlyDateWithDOW)}
        subTitle={getPaymentDateSubTitle()}
        subTitleColor={'textDisabled'}
        actionRowInfoModal={{
          title: t('HowLongDoPaymentsTake'),
          content: (
            <Box gap={'medium'}>
              <PFText variant="p">{t('PaymentsMadeBy3PM')}</PFText>
              <PFText variant="p">{t('PaymentsMadeAfter3PM')}</PFText>
            </Box>
          ),
          onOkay: () => {
            TrackAppEvent(
              ManageActiveLoanEvents.make_payment_confirm_info_modal_cta,
              AppEvents.Category.ManageActiveLoan,
            )
          },
          onPressShowInfoModal: () => {
            TrackAppEvent(
              ManageActiveLoanEvents.make_payment_confirm_info_modal_viewed,
              AppEvents.Category.ManageActiveLoan,
            )
          },
        }}
      />

      <Box gap={'medium'} paddingHorizontal={'medium'} paddingTop={'medium'} grow>
        {selectedPaymentOption?.intention === LoanCustomPaymentIntention.FullBalance ? (
          <PFInfoCapsule
            svgIcon={{name: 'money', colorVariant: 'success', size: 'medium'}}
            text={t('WhenPayingFullBalance')}
          />
        ) : null}
        {selectedPaymentMethod ? (
          <Box flex={1} justify={'end'} paddingHorizontal={'small'}>
            <PFText variant="p_sm" color={NamedColors.SILVER}>
              {t('WhenYouTapPayNow')}
            </PFText>
          </Box>
        ) : null}
      </Box>
    </Page>
  )
}

export {MakePaymentConfirmTemplate}
